import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import _ from 'lodash';
import * as Yup from 'yup';

import {
    DefaultButton,
    PrimaryButton,
    Panel,
    PanelType,
    Separator,
    Stack,
    IconButton
} from '@fluentui/react';

import { RootState } from '../../../redux/application/redux-store';
import { PropertyAttributeTypeEnum, TicketFilteringCriteria, TicketPriorityEnum, TicketStatusEnum } from '../../../api-client';

import { TRANSLATION_KEYS } from './localization/translation-keys';
import { useFieldArray, useForm } from 'react-hook-form';
import { FormProvider, IAIComboBox, IAIDatePicker, IAITextField } from '../IAIForm';
import { yupResolver } from '@hookform/resolvers/yup';

interface IIAITicketsRefinementDrawer {
    criteria?: TicketFilteringCriteria;
    disabled?: boolean;
    drawerVisible?: boolean;
    onApplyCriteria: (filterCriteria: TicketFilteringCriteria | undefined) => void;
    onClearCriteria: () => void;
    onDrawerDismiss: () => void;
}

interface FormValuesProps extends TicketFilteringCriteria {
    customAttributesVertical: {
        _k: string;
        id: string;
        areaId?: string;
        departmentId?: string;
        label: string;
        value?: any;
        options?: string[];
        type: PropertyAttributeTypeEnum;
    }[];
}

const IAITicketsRefinementDrawer: React.FunctionComponent<IIAITicketsRefinementDrawer> = ({
    criteria,
    disabled,
    drawerVisible,
    onApplyCriteria,
    onClearCriteria,
    onDrawerDismiss
}) => {
    //#region Dropdown-Management
    const iaiSystem = useSelector((state: RootState) => state.system);

    const areaOptions = useMemo(
        () => (
            _.map(iaiSystem.areas, entry => ({ key: entry.id!, text: entry.displayName! }))
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [iaiSystem.areas]
    );

    const departmentOptions = useMemo(
        () => (
            _.map(iaiSystem.departments, entry => ({ key: entry.id!, text: entry.displayName! }))
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [iaiSystem.departments]
    );

    const agentOptions = useMemo(
        () => (
            _.map(iaiSystem.agents, entry => ({ key: entry.id!, text: entry.displayName! }))
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [iaiSystem.agents]
    );

    const customAttributesOptions = useMemo(
        () => (
            _.map(iaiSystem.customAttributes, entry => entry)
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [iaiSystem.customAttributes]
    );

    //#endregion

    const {
        t: translate
    } = useTranslation();

    const formDataSchema = Yup.object().shape({
        fromDate: Yup.string(),
        toDate: Yup.string(),
        areaId: Yup.string(),
        departmentIdList: Yup.array().of(Yup.string()),
        agentIdList: Yup.array().of(Yup.string()),
        statusList: Yup.array().of(Yup.string()),
        priority: Yup.string(),
        customAttributesVertical: Yup.array(),
    });

    const defaultValues = React.useMemo(
        () => ({
            fromDate: undefined,
            toDate: undefined,
            areaId: "",
            departmentIdList: [],
            agentIdList: [],
            statusList: [],
            priority: "",
            customAttributesVertical: _.map(customAttributesOptions, dataItem => {
                return ({
                    id: dataItem.id,
                    areaId: dataItem.areaId,
                    departmentId: dataItem.departmentId,
                    label: dataItem.label,
                    value: dataItem.attributeType === PropertyAttributeTypeEnum.Date
                        ? undefined
                        : '',
                    options: dataItem.options || [],
                    type: dataItem.attributeType || PropertyAttributeTypeEnum.Combo
                });
            })
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [customAttributesOptions]
    );

    const methods = useForm<FormValuesProps>({ resolver: yupResolver(formDataSchema), defaultValues });

    const {
        control,
        handleSubmit,
        reset,
        watch
    } = methods;

    const {
        fields,
    } = useFieldArray({
        control,
        name: 'customAttributesVertical'
    });


    useEffect(() => {
        reset(defaultValues);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customAttributesOptions]);

    const formValues = watch();

    const _handleFilter = ({ customAttributesVertical, ...data }: FormValuesProps) => {
        const customAttributesPayload = _.map(_.filter(customAttributesVertical, x => !!x.value), x => ({
            id: x.id,
            value: x.type === PropertyAttributeTypeEnum.Date
                ? moment(x.value)
                    .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
                    .format('YYYY-MM-DD')
                : x.value,
        }));

        const filterCriteria = {
            ...data,
            fromDate: !!data.fromDate
                ? moment(data.fromDate).endOf('day').toDate().toISOString()
                : undefined,
            toDate: !!data.toDate
                ? moment(data.toDate).endOf('day').toDate().toISOString()
                : undefined,
            properties: customAttributesPayload
        }

        onApplyCriteria(filterCriteria);
    }

    const _handleClear = () => {
        reset(defaultValues);
        onClearCriteria();
    }

    return (
        <Panel
            type={PanelType.smallFixedFar}
            headerText={translate(TRANSLATION_KEYS.Heading)}
            isOpen={drawerVisible}
            isBlocking={true}
            isFooterAtBottom={true}
            onDismiss={onDrawerDismiss}
            onRenderFooterContent={() => (
                <div className="text-end">
                    <DefaultButton
                        disabled={disabled}
                        iconProps={{ iconName: 'Delete' }}
                        styles={{ root: { marginRight: 10 } }}
                        onClick={_handleClear}
                    >
                        {translate(TRANSLATION_KEYS.DismissText)}
                    </DefaultButton>
                    <PrimaryButton
                        type="submit"
                        disabled={disabled}
                        iconProps={{ iconName: 'Search' }}
                        onClick={handleSubmit(_handleFilter)}
                    >
                        {translate(TRANSLATION_KEYS.ConfirmText)}
                    </PrimaryButton>
                </div>
            )}
        >
            <Separator />

            <FormProvider methods={methods}>
                <Stack tokens={{ childrenGap: 15 }}>
                    <IAIComboBox
                        name="areaId"
                        label={translate(TRANSLATION_KEYS.AreaLabel)}
                        placeholder={translate(TRANSLATION_KEYS.AreaPlaceholder)}
                        allowFreeform={true}
                        autoComplete="on"
                        disabled={disabled || !iaiSystem.areasInitialized}
                        options={areaOptions}>
                    </IAIComboBox>
                    <IAIComboBox
                        name="departmentIdList"
                        label={translate(TRANSLATION_KEYS.DepartmentLabel)}
                        placeholder={translate(TRANSLATION_KEYS.DepartmentPlaceholder)}
                        allowFreeform={true}
                        autoComplete="on"
                        disabled={disabled || !iaiSystem.departmentsInitialized}
                        options={departmentOptions}
                        multiSelect
                    />
                    <IAIComboBox
                        name="agentIdList"
                        label={translate(TRANSLATION_KEYS.AgentLabel)}
                        placeholder={translate(TRANSLATION_KEYS.AgentPlaceholder)}
                        allowFreeform={true}
                        autoComplete="on"
                        disabled={disabled || !iaiSystem.agentsInitialized}
                        options={agentOptions}
                        multiSelect
                    />
                    {!!fields.length && (
                        <div className="row">
                            {_.map(fields, (dataItem, dataIndex) => {
                                const isVisible = (!dataItem.areaId && !dataItem.departmentId) ||
                                    (formValues.areaId === dataItem.areaId && (dataItem.departmentId && (formValues.departmentIdList ?? []).indexOf(dataItem.departmentId) !== -1));

                                if (isVisible) {
                                    return (
                                        <div key={dataItem.id} className="col-12">
                                            {dataItem.type === PropertyAttributeTypeEnum.Combo && (
                                                <IAIComboBox
                                                    name={`customAttributesVertical[${dataIndex}].value`}
                                                    label={dataItem.label}
                                                    placeholder={dataItem.label}
                                                    allowFreeform={true}
                                                    autoComplete="on"
                                                    disabled={disabled || !iaiSystem.customAttributesInitialized}
                                                    options={_.map(dataItem.options, x => ({ key: x, text: x }))}
                                                />
                                            )}
                                            {dataItem.type === PropertyAttributeTypeEnum.Date && (
                                                <IAIDatePicker
                                                    name={`customAttributesVertical[${dataIndex}].value`}
                                                    label={dataItem.label}
                                                    placeholder={dataItem.label}
                                                    disabled={disabled || !iaiSystem.customAttributesInitialized}
                                                />
                                            )}
                                            {dataItem.type === PropertyAttributeTypeEnum.Input && (
                                                <IAITextField
                                                    name={`customAttributesVertical[${dataIndex}].value`}
                                                    label={dataItem.label}
                                                    placeholder={dataItem.label}
                                                    disabled={disabled || !iaiSystem.customAttributesInitialized}
                                                />
                                            )}
                                        </div>
                                    );
                                }
                            })}
                        </div>
                    )}
                    <IAIComboBox
                        name="statusList"
                        label={translate(TRANSLATION_KEYS.StatusLabel)}
                        placeholder={translate(TRANSLATION_KEYS.StatusPlaceholder)}
                        allowFreeform={true}
                        autoComplete="on"
                        disabled={disabled}
                        options={[
                            { key: TicketStatusEnum.Canceled, text: translate(TRANSLATION_KEYS.StatusOptionCanceled) },
                            { key: TicketStatusEnum.Closed, text: translate(TRANSLATION_KEYS.StatusOptionClosed) },
                            { key: TicketStatusEnum.Deleted, text: translate(TRANSLATION_KEYS.StatusOptionDeleted) },
                            { key: TicketStatusEnum.Open, text: translate(TRANSLATION_KEYS.StatusOptionOpen) },
                            { key: TicketStatusEnum.Pending, text: translate(TRANSLATION_KEYS.StatusOptionPending) },
                            { key: TicketStatusEnum.Working, text: translate(TRANSLATION_KEYS.StatusOptionWorking) },
                        ]}
                        multiSelect
                    />
                    <IAIComboBox
                        name="priority"
                        label={translate(TRANSLATION_KEYS.PriorityLabel)}
                        placeholder={translate(TRANSLATION_KEYS.PriorityPlaceholder)}
                        allowFreeform={true}
                        autoComplete="on"
                        disabled={disabled}
                        options={[
                            { key: TicketPriorityEnum.Low, text: translate(TRANSLATION_KEYS.PriorityOptionLow) },
                            { key: TicketPriorityEnum.Medium, text: translate(TRANSLATION_KEYS.PriorityOptionMedium) },
                            { key: TicketPriorityEnum.High, text: translate(TRANSLATION_KEYS.PriorityOptionHigh) }
                        ]}
                    />
                    <IAIDatePicker
                        name="fromDate"
                        label={translate(TRANSLATION_KEYS.FromDateLabel)}
                        placeholder={translate(TRANSLATION_KEYS.FromDatePlaceholder)}
                        disabled={disabled}
                    />
                    <IAIDatePicker
                        name="toDate"
                        label={translate(TRANSLATION_KEYS.ToDateLabel)}
                        placeholder={translate(TRANSLATION_KEYS.ToDatePlaceholder)}
                        disabled={disabled}
                    />
                </Stack>
            </FormProvider>
        </Panel>
    );
}

export default IAITicketsRefinementDrawer;